import { Sdk, User } from "../../core/internal/model/model"
import { DevToolsManager } from "../devtools/DevTools"
import { Config } from "../../index.browser"
import {
  BROWSER_MIN_POOL_INTERVAL,
  DEFAULT_CDN_URL,
  DEFAULT_SDK_URL,
  DEFAULT_DEDUP_INTERVAL_MILLIS,
  DEFAULT_EVENT_URL,
  DEFAULT_MONITORING_URL,
  DEFAULT_SESSION_TIMEOUT_MILLIS,
  IAM_RENDERER_VERSION,
  NO_POLLING,
  SDK_VERSION
} from "../../config"
import Logger, { logLevels } from "../../core/internal/logger"
import { Metrics } from "../../core/internal/metrics/Metrics"
import { SessionPolicy } from "../../core/internal/session/SessionPolicy"
import { HackleSessionPersistConditions } from "../../public/model"
import ObjectUtil from "../../core/internal/util/ObjectUtil"
import { EvaluationMode } from "../../public/model"

export interface InternalConfig {
  user?: User

  debug: boolean
  log_disabled: boolean
  evaluationMode: EvaluationMode
  automaticPageTracking: boolean
  automaticRouteTracking: boolean
  pollingIntervalMillis: number
  exposureEventDedupIntervalMillis: number
  sessionPolicy: SessionPolicy
  devTool?: DevToolsManager
  autoOpenDevTool?: boolean

  sdkUrl: string
  eventUrl: string
  monitoringUrl: string
  enableMonitoring: boolean
  cdnUrl: string
  sameSiteCookie: string
  secureCookie: boolean
  optOutTracking: boolean

  SDK_NAME_HEADER: string
  SDK_VERSION_HEADER: string

  IAM_RENDERER_VERSION_HEADER: string
}

const DEFAULT_CONFIG: InternalConfig = {
  user: undefined,

  debug: false,
  log_disabled: false,
  evaluationMode: "local",
  automaticPageTracking: true,
  automaticRouteTracking: true,
  pollingIntervalMillis: NO_POLLING,
  exposureEventDedupIntervalMillis: DEFAULT_DEDUP_INTERVAL_MILLIS,
  sessionPolicy: {
    timeoutMillis: DEFAULT_SESSION_TIMEOUT_MILLIS,
    persistCondition: HackleSessionPersistConditions.ALWAYS_NEW_SESSION
  },
  autoOpenDevTool: false,

  sdkUrl: DEFAULT_SDK_URL,
  eventUrl: DEFAULT_EVENT_URL,
  monitoringUrl: DEFAULT_MONITORING_URL,
  enableMonitoring: true,
  cdnUrl: DEFAULT_CDN_URL,
  sameSiteCookie: "LAX",
  secureCookie: false,
  optOutTracking: false,

  SDK_NAME_HEADER: "javascript-sdk_browser",
  SDK_VERSION_HEADER: SDK_VERSION,
  IAM_RENDERER_VERSION_HEADER: IAM_RENDERER_VERSION
}

export class Configurations {
  static config = (config?: Config): InternalConfig => {
    return {
      ...DEFAULT_CONFIG,
      ...config,
      evaluationMode: Configurations.resolveEvaluationMode(config),
      automaticPageTracking: Configurations.resolveAutomaticPageTracking(config),
      sessionPolicy: Configurations.resolveSessionPolicy(config)
    }
  }

  private static readonly EVALUATION_MODES: readonly EvaluationMode[] = ["local", "remote"]

  private static resolveEvaluationMode = (config?: Config): EvaluationMode => {
    const evaluationMode = config?.evaluationMode
    if (evaluationMode === undefined) {
      return DEFAULT_CONFIG.evaluationMode
    }
    if (!Configurations.EVALUATION_MODES.includes(evaluationMode as EvaluationMode)) {
      Logger.log.warn(
        `Invalid evaluation mode[${evaluationMode}]. Setting to default value[${DEFAULT_CONFIG.evaluationMode}].`
      )
      return DEFAULT_CONFIG.evaluationMode
    }
    return evaluationMode
  }

  private static resolveSessionPolicy = (config?: Config): SessionPolicy => {
    if (config?.sessionPolicy) {
      return {
        timeoutMillis: Configurations.resolveTimeoutMillis(config.sessionPolicy.timeoutMillis),
        persistCondition: Configurations.resolvePersistCondition(config.sessionPolicy.persistCondition)
      }
    }
    if (config && ObjectUtil.isNotNullOrUndefined(config.sessionTimeoutMillis)) {
      return {
        timeoutMillis: Configurations.resolveTimeoutMillis(config.sessionTimeoutMillis),
        persistCondition: HackleSessionPersistConditions.ALWAYS_NEW_SESSION
      }
    }
    return DEFAULT_CONFIG.sessionPolicy
  }

  private static resolveTimeoutMillis = (value: unknown): number => {
    const timeoutMillis = value ?? DEFAULT_SESSION_TIMEOUT_MILLIS
    if (!Number.isFinite(timeoutMillis) || (timeoutMillis as number) <= 0) {
      Logger.log.warn("Session policy timeoutMillis is invalid. Setting to default value[30m].")
      return DEFAULT_SESSION_TIMEOUT_MILLIS
    }
    return timeoutMillis as number
  }

  private static resolvePersistCondition = (value: unknown): SessionPolicy["persistCondition"] => {
    if (typeof value === "function") {
      return value as SessionPolicy["persistCondition"]
    }
    if (ObjectUtil.isNotNullOrUndefined(value)) {
      Logger.log.warn("Session policy persistCondition is not a function. Using default (ALWAYS_NEW_SESSION).")
    }
    return HackleSessionPersistConditions.ALWAYS_NEW_SESSION
  }

  private static resolveAutomaticPageTracking = (config?: Config): boolean => {
    if (typeof config?.automaticPageTracking !== "boolean" && typeof config?.auto_track_page_view !== "boolean") {
      return DEFAULT_CONFIG.automaticPageTracking
    }

    return !!config.automaticPageTracking || !!config.auto_track_page_view
  }

  static logging = (config: InternalConfig) => {
    Logger.initCounter((level) => Metrics.counter("log", { level: level }))

    Logger.setLogLevel(logLevels.INFO)
    if (config.log_disabled) {
      Logger.setLogLevel(logLevels.DISABLE)
    } else {
      if (config.debug) {
        Logger.setLogLevel(logLevels.DEBUG)
      }
    }
  }

  static sdk = (sdkKey: string, config: InternalConfig): Sdk => {
    Logger.log.debug(`SDK key: ${sdkKey}`)
    if (!sdkKey) {
      Logger.log.error(`invalid sdk key: '${sdkKey}' (expected: not null, not undefined and not empty string)`)
    }
    return {
      key: sdkKey,
      name: config.SDK_NAME_HEADER,
      version: config.SDK_VERSION_HEADER
    }
  }

  static isBeaconSupported = (): boolean => {
    let useBeacon = false
    if (typeof window !== "undefined") {
      useBeacon = window && window.navigator && !!window.navigator.sendBeacon && true
      if (useBeacon) {
        Logger.log.debug("support sendBeacon API")
      }
    }
    return useBeacon
  }

  static pollingIntervalMillis = (config: InternalConfig): number => {
    if (config.pollingIntervalMillis === undefined) {
      return NO_POLLING
    }

    // noinspection SuspiciousTypeOfGuard
    if (typeof config.pollingIntervalMillis !== "number") {
      return NO_POLLING
    }

    if (config.pollingIntervalMillis <= 0) {
      return NO_POLLING
    }

    return Math.max(config.pollingIntervalMillis, BROWSER_MIN_POOL_INTERVAL)
  }

  static dedupIntervalMillis = (config: InternalConfig): number => {
    if (config.exposureEventDedupIntervalMillis === undefined) {
      return DEFAULT_DEDUP_INTERVAL_MILLIS
    }

    // noinspection SuspiciousTypeOfGuard
    if (typeof config.exposureEventDedupIntervalMillis !== "number") {
      return DEFAULT_DEDUP_INTERVAL_MILLIS
    }

    if (config.exposureEventDedupIntervalMillis < 1000 || config.exposureEventDedupIntervalMillis > 1000 * 60 * 60) {
      Logger.log.warn(
        "Exposure event dedup interval is outside allowed range[1_000ms..3_600_000ms]. Setting to default value[1m]."
      )
      return DEFAULT_DEDUP_INTERVAL_MILLIS
    }

    return config.exposureEventDedupIntervalMillis
  }
}
